import { useState, useEffect, useRef, useImperativeHandle, forwardRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence, useMotionValue, useTransform, animate } from 'framer-motion';
import {
  Plus,
  Minus,
  ChevronDown,
  Pin,
  List,
  Lock,
  Globe,
  Pencil,
  Check,
  Trash2,
  Clock,
  RotateCcw,
  CalendarDays,
  ExternalLink,
} from 'lucide-react';
import {
  FilterPreset,
  ActiveListItem,
  CustomList,
  UserEvent,
  ExpandedVendor,
  ToastType,
  GCalEvent,
  PinnedEvent,
} from '../../types/types';
import DateStrip from './DateStrip';
import { Share2 } from 'lucide-react';
import { CuratedListMeta } from '../../lib/vendorApi';
import { isActiveListItemOutdated, isEventOutdated } from '../../lib/vendorUtils';
import { format } from 'date-fns';
import VendorCard from '../Cards/VendorCard';

const LIST_PANEL_HEIGHT = 'calc(65vh - 120px)';
const PILL_MAX_WIDTH = '110px';
const VISIBLE_PILLS = 3;
const SWIPE_THRESHOLD = 50; // bottom sheet drag threshold (vertical)
const CARD_SWIPE_COMMIT = 80; // card swipe commit threshold (horizontal)

const THEME = {
  borderRadius: '20px 20px 0 0',
  shadow: '0 -4px 24px rgba(0, 0, 0, 0.12)',
  handle: 'var(--color-border-strong)',
  activePill: 'var(--color-primary)',
  inactivePill: 'var(--color-neutral)',
  activeText: 'var(--color-content-inverse)',
  inactiveText: 'var(--color-content)',
  listBg: 'var(--color-surface-subtle)',
  listBorder: 'var(--color-border)',
  outdatedBg: 'rgba(220, 50, 50, 0.055)' /* no token — intentionally subtle red tint */,
};

const PRESET_LABELS: Record<string, string> = {
  active: 'movement',
  music: 'music',
  'event+pop-up': 'culture',
};

interface BottomSheetProps {
  isMobile: boolean;
  filterPresets: FilterPreset[];
  onTogglePreset: (id: string) => void;
  currentDate: Date;
  onDateChange: (date: Date) => void;
  availableDates: Array<{ date: Date; dateStr: string; vendorCount: number }>;
  curatedLists: CuratedListMeta[];
  onAddList: (meta: CuratedListMeta) => void;
  onRemovePreset: (listUuid: string) => void;
  onShareList: (listUuid: string) => void;
  onHeightChange?: (height: number) => void;
  shareViewState?: {
    active: boolean;
    eventId: number;
    date: Date;
    coordinates: [number, number];
    isPast: boolean;
    isFuture: boolean;
  } | null;
  // List-of-lists model
  userLists: CustomList[];
  listItemsCache: Record<number, ActiveListItem[]>;
  onExpandList: (id: number) => void;
  curatedItemsCache: Record<string, ActiveListItem[]>;
  onExpandCuratedList: (uuid: string) => void;
  activeListId: number | null;
  savedIndicatorListId: number | null;
  onCreateList: () => Promise<number>;
  onDeleteList: (listId: number) => void;
  onSetActiveList: (listId: number) => void;
  onAddUserList: (list: CustomList) => void;
  onRemoveItem: (itemId: number, listId: number) => void;
  onMoveItem: (
    itemId: number,
    sourceListId: number,
    targetListId: number,
    item: ActiveListItem
  ) => void;
  onRenameList: (listId: number, newTitle: string) => void;
  onToggleVisibility: (listId: number) => void;
  // My Events tab
  userEvents?: UserEvent[];
  pinnedEvents?: PinnedEvent[];
  onEditEvent?: (event: UserEvent) => void;
  onDeleteUserEvent?: (id: number) => void;
  onSubmitForReview?: (id: number) => void;
  onRefreshEvents?: () => void;
  calendarEvents?: GCalEvent[];
  calendarEventsLoading?: boolean;
  calendarEventsError?: string | null;
  onCalendarReconnect?: () => void;
  // Discover tab — vendor feed
  mapVendors: ExpandedVendor[];
  isInitialLoad: boolean;
  isVendorFeedLoading: boolean;
  selectedVendorId: number | null;
  selectedDisplayIds: string[];
  onVendorSelect: (vendorId: number, displayId: string) => void;
  activeListItems: ActiveListItem[];
  showToast: (message: string, type?: ToastType, duration?: number, dismissible?: boolean) => void;
  onSignUpModalRequest: (headerText: string) => void;
  onShareEvent: (vendorId: number, date: Date, coordinates: [number, number]) => void;
  onPinEvent: (
    vendorId: number,
    eventDate: string,
    vendorName: string,
    vendorCategory: string
  ) => Promise<number | null>;
  onUnpinEvent: (itemId: number) => Promise<boolean>;
}

export interface BottomSheetHandle {
  openToLevel: (level: SheetLevel) => void;
  switchToTab: (tab: ListTab) => void;
}

type SheetLevel = 0 | 1 | 2;
type ListTab = 'discover' | 'lists' | 'myevents';

// ─── FilterPill ───────────────────────────────────────────────────────────────

function FilterPill({ preset, onToggle }: { preset: FilterPreset; onToggle: () => void }) {
  const label =
    preset.type === 'category' ? (PRESET_LABELS[preset.value] ?? preset.value) : preset.label;
  return (
    <motion.div
      role="button"
      tabIndex={0}
      aria-pressed={preset.active}
      aria-label={label}
      onClick={onToggle}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggle();
        }
      }}
      whileTap={{ scale: 0.93 }}
      style={{
        backgroundColor: preset.active ? THEME.activePill : THEME.inactivePill,
        color: preset.active ? THEME.activeText : THEME.inactiveText,
        borderRadius: '20px',
        padding: '6px 14px',
        fontSize: '0.8rem',
        display: 'flex',
        alignItems: 'center',
        letterSpacing: '1px',
        textTransform: 'lowercase',
        cursor: 'pointer',
        userSelect: 'none',
        flexShrink: 0,
        maxWidth: PILL_MAX_WIDTH,
        boxShadow: 'var(--shadow-sm)',
        transition: 'all 0.2s ease',
        minHeight: 'unset',
        minWidth: 'unset',
      }}
    >
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {label}
      </span>
    </motion.div>
  );
}

// ─── MyListRow ───────────────────────────────────────────────────────────────

interface MyListRowProps {
  item: ActiveListItem;
  listId: number;
  otherLists: CustomList[];
  onRemove?: () => void;
  onMove?: (targetListId: number) => void;
  onCreateAndMove?: () => void;
  isOutdated?: boolean;
}

const UNDO_WINDOW_MS = 3000;

function MyListRow({
  item,
  otherLists,
  onRemove,
  onMove,
  onCreateAndMove,
  isOutdated,
}: MyListRowProps) {
  const [showPicker, setShowPicker] = useState(false);
  const [pickerRect, setPickerRect] = useState<{ top: number; left: number; width: number } | null>(
    null
  );
  const [isPendingRemove, setIsPendingRemove] = useState(false);
  const rowRef = useRef<HTMLDivElement>(null);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const removeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const touchStart = useRef<{ x: number; y: number } | null>(null);

  const handlePendingRemove = () => {
    setIsPendingRemove(true);
    removeTimer.current = setTimeout(() => {
      onRemove?.();
    }, UNDO_WINDOW_MS);
  };

  const handleUndo = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
    if (removeTimer.current) clearTimeout(removeTimer.current);
    setIsPendingRemove(false);
  };

  const isRecurring = item.vendor_schedule?.repeat && item.vendor_schedule.repeat !== 'once';
  const dateDisplay = isRecurring
    ? `repeats ${item.vendor_schedule!.repeat}`
    : item.event_date
      ? new Date(item.event_date + 'T00:00:00').toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
        })
      : '';

  const handleTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    touchStart.current = { x: t.clientX, y: t.clientY };
    longPressTimer.current = setTimeout(() => {
      if (rowRef.current) {
        const r = rowRef.current.getBoundingClientRect();
        setPickerRect({ top: r.bottom, left: r.left, width: r.width });
      }
      setShowPicker(true);
    }, 500);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStart.current) return;
    const t = e.touches[0];
    const dx = Math.abs(t.clientX - touchStart.current.x);
    const dy = Math.abs(t.clientY - touchStart.current.y);
    if (dx > 10 || dy > 10) {
      clearTimeout(longPressTimer.current!);
      longPressTimer.current = null;
    }
  };

  const handleTouchEnd = () => {
    clearTimeout(longPressTimer.current!);
    longPressTimer.current = null;
  };

  const closePicker = () => {
    setShowPicker(false);
    setPickerRect(null);
  };

  return (
    <div
      ref={rowRef}
      style={{
        position: 'relative',
        padding: '10px 16px 10px 20px',
        borderBottom: `1px solid ${THEME.listBorder}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        background: isOutdated ? THEME.outdatedBg : 'var(--color-surface-subtle)',
        userSelect: 'none',
        overflow: 'hidden',
      }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Row content — fades slowly when pending removal */}
      <motion.div
        animate={{ opacity: isPendingRemove ? 0.15 : 1 }}
        transition={{ duration: isPendingRemove ? UNDO_WINDOW_MS / 1000 : 0.2, ease: 'linear' }}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          flex: 1,
          minWidth: 0,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: '0.85rem',
              letterSpacing: '1px',
              textTransform: 'lowercase',
              color: 'var(--color-content)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {item.vendor_name}
          </div>
          <div
            style={{
              fontSize: '0.72rem',
              color: 'var(--color-content-muted)',
              letterSpacing: '0.5px',
            }}
          >
            {dateDisplay} · {item.vendor_category}
          </div>
        </div>
        {onRemove && (
          <motion.div
            whileTap={{ scale: 0.9 }}
            onClick={isPendingRemove ? undefined : handlePendingRemove}
            style={{
              width: '26px',
              height: '26px',
              borderRadius: '50%',
              backgroundColor: 'rgba(34, 197, 94, 0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: isPendingRemove ? 'default' : 'pointer',
              flexShrink: 0,
            }}
          >
            <Pin size={12} color="rgba(34, 197, 94, 1)" fill="rgba(34, 197, 94, 1)" />
          </motion.div>
        )}
      </motion.div>

      {/* Undo overlay — appears while removal is pending */}
      <AnimatePresence>
        {isPendingRemove && (
          <motion.div
            key="undo-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2,
            }}
          >
            <motion.button
              whileTap={{ scale: 0.92 }}
              onClick={handleUndo}
              onTouchEnd={handleUndo}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                backgroundColor: 'var(--color-primary)',
                color: 'var(--color-content-inverse)',
                borderRadius: '20px',
                padding: '5px 12px',
                fontSize: '11px',
                letterSpacing: '0.5px',
                border: 'none',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
              }}
            >
              <RotateCcw size={11} />
              undo
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>

      {showPicker &&
        pickerRect &&
        createPortal(
          <>
            {/* Backdrop */}
            <div style={{ position: 'fixed', inset: 0, zIndex: 9995 }} onClick={closePicker} />
            {/* Popover — fixed, anchored to row bottom edge */}
            <div
              style={{
                position: 'fixed',
                top: pickerRect.top + 4,
                left: pickerRect.left,
                width: pickerRect.width,
                zIndex: 9996,
                background: 'var(--color-surface)',
                borderRadius: 10,
                boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  padding: '10px 16px 8px',
                  fontSize: 10,
                  color: 'var(--color-content-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  borderBottom: `1px solid ${THEME.listBorder}`,
                }}
              >
                Move to
              </div>
              {otherLists.map(list => (
                <button
                  key={list.id}
                  onClick={() => {
                    onMove?.(list.id);
                    closePicker();
                  }}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '12px 16px',
                    border: 'none',
                    background: 'transparent',
                    borderBottom: `1px solid ${THEME.listBorder}`,
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    letterSpacing: '1px',
                    textTransform: 'lowercase',
                    color: 'var(--color-content)',
                  }}
                >
                  {list.title || 'my list'}
                </button>
              ))}
              <button
                onClick={() => {
                  onCreateAndMove?.();
                  closePicker();
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '12px 16px',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  letterSpacing: '1px',
                  color: THEME.activePill,
                }}
              >
                ＋ New list
              </button>
            </div>
          </>,
          document.body
        )}
    </div>
  );
}

// ─── DeleteConfirmPortal ──────────────────────────────────────────────────────

function DeleteConfirmPortal({
  label,
  onConfirm,
  onCancel,
}: {
  label: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return createPortal(
    <>
      <div
        style={{ position: 'fixed', inset: 0, zIndex: 10010, background: 'rgba(0,0,0,0.35)' }}
        onClick={onCancel}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Confirm delete"
        style={{
          position: 'fixed',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 10011,
          background: 'var(--color-surface)',
          borderRadius: 16,
          padding: '24px 20px',
          minWidth: 240,
          boxShadow: '0 8px 32px rgba(0,0,0,0.22)',
          textAlign: 'center',
        }}
      >
        <p style={{ fontSize: '0.9rem', letterSpacing: '1px', marginBottom: 16 }}>{label}</p>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
          <motion.button
            whileTap={{ scale: 0.93 }}
            onClick={onCancel}
            style={{
              background: THEME.inactivePill,
              border: 'none',
              borderRadius: 12,
              padding: '6px 16px',
              fontSize: '0.75rem',
              letterSpacing: '1px',
              cursor: 'pointer',
            }}
          >
            cancel
          </motion.button>
          <motion.button
            whileTap={{ scale: 0.93 }}
            onClick={onConfirm}
            style={{
              background: 'var(--color-error)',
              border: 'none',
              borderRadius: 12,
              padding: '6px 16px',
              fontSize: '0.75rem',
              letterSpacing: '1px',
              color: 'var(--color-content-inverse)',
              cursor: 'pointer',
            }}
          >
            delete
          </motion.button>
        </div>
      </div>
    </>,
    document.body
  );
}

// ─── MyEventRow ──────────────────────────────────────────────────────────────

const VISIBILITY_BADGE: Record<string, string> = {
  private: 'Private',
  pending_review: 'Pending Review',
  public: 'Public',
};

interface MyEventRowProps {
  event: UserEvent;
  onEdit?: (event: UserEvent) => void;
  onDelete?: (id: number) => void;
  onSubmitForReview?: (id: number) => void;
}

const EVENT_DATE_FORMAT: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };

function formatEventDate(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-GB', EVENT_DATE_FORMAT);
}

function formatEventDateRange(start: string | null, end: string | null): string {
  const s = formatEventDate(start);
  const e = formatEventDate(end);
  if (!s && !e) return '';
  if (!e || s === e) return s;
  return `${s} → ${e}`;
}

function VisibilityIcon({
  visibility,
  isSubmitting,
  onSubmit,
}: {
  visibility: string;
  isSubmitting: boolean;
  onSubmit: () => void;
}) {
  const icon =
    visibility === 'public' ? (
      <Globe size={13} color={THEME.activePill} />
    ) : visibility === 'pending_review' ? (
      <Clock size={13} color="var(--color-forest-bright)" />
    ) : (
      <Lock size={13} color="var(--color-content-muted)" />
    );

  const clickable = visibility === 'private' && !isSubmitting;

  return (
    <motion.div
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-label={clickable ? 'submit for review' : VISIBILITY_BADGE[visibility]}
      whileTap={clickable ? { scale: 0.9 } : undefined}
      onClick={clickable ? onSubmit : undefined}
      onKeyDown={
        clickable
          ? e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSubmit();
              }
            }
          : undefined
      }
      style={{
        width: '28px',
        height: '28px',
        borderRadius: '50%',
        backgroundColor: THEME.inactivePill,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: clickable ? 'pointer' : 'default',
        flexShrink: 0,
      }}
    >
      {icon}
    </motion.div>
  );
}

function MyEventRow({ event, onEdit, onDelete, onSubmitForReview }: MyEventRowProps) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(false);

  const x = useMotionValue(0);
  const deleteUnderlayOpacity = useTransform(x, [-CARD_SWIPE_COMMIT, 0], [1, 0]);

  const dateDisplay = formatEventDateRange(event.start_date, event.expiry);
  const isOutdated = isEventOutdated(event);

  const handleDragEnd = (_: any, info: { offset: { x: number } }) => {
    if (info.offset.x <= -CARD_SWIPE_COMMIT) {
      animate(x, 0, { type: 'spring', stiffness: 400, damping: 35 });
      setPendingDelete(true);
    } else {
      animate(x, 0, { type: 'spring', stiffness: 400, damping: 35 });
    }
  };

  const handleDeleteConfirmed = async () => {
    setIsDeleting(true);
    setPendingDelete(false);
    await onDelete?.(event.id);
    setIsDeleting(false);
  };

  const handleSubmitForReview = async () => {
    setIsSubmitting(true);
    await onSubmitForReview?.(event.id);
    setIsSubmitting(false);
  };

  return (
    <div style={{ position: 'relative', overflow: 'hidden' }}>
      {/* Delete underlay */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
        <motion.div
          style={{
            opacity: deleteUnderlayOpacity,
            background: 'var(--color-error)',
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            paddingRight: '20px',
            gap: '8px',
          }}
        >
          <span
            style={{
              fontSize: '0.75rem',
              letterSpacing: '1px',
              color: 'var(--color-content-inverse)',
              textTransform: 'lowercase',
            }}
          >
            delete
          </span>
          <Trash2 size={14} color="var(--color-content-inverse)" />
        </motion.div>
      </div>

      {/* Draggable content */}
      <motion.div
        drag="x"
        dragConstraints={{ left: -150, right: 0 }}
        dragElastic={0.15}
        style={{ x }}
        onDragEnd={handleDragEnd}
      >
        <div
          style={{
            padding: '10px 16px 10px 20px',
            borderBottom: `1px solid ${THEME.listBorder}`,
            background: isOutdated ? THEME.outdatedBg : 'var(--color-surface-subtle)',
            opacity: isDeleting || isSubmitting ? 0.5 : 1,
            transition: 'opacity 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          {/* Main content */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Row 1: name + category text */}
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: '6px',
                marginBottom: '3px',
                minWidth: 0,
              }}
            >
              <span
                style={{
                  fontSize: '0.85rem',
                  fontWeight: 'bold',
                  letterSpacing: '1px',
                  textTransform: 'lowercase',
                  color: 'var(--color-content)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flexShrink: 1,
                  minWidth: 0,
                }}
              >
                {event.name}
              </span>
              <span
                style={{
                  fontSize: '0.68rem',
                  letterSpacing: '0.5px',
                  textTransform: 'lowercase',
                  color: 'var(--color-content-muted)',
                  flexShrink: 0,
                }}
              >
                {event.category}
              </span>
            </div>

            {/* Row 2: date range */}
            {dateDisplay && (
              <div
                style={{
                  fontSize: '0.72rem',
                  color: 'var(--color-content-muted)',
                  letterSpacing: '0.5px',
                }}
              >
                {dateDisplay}
              </div>
            )}
          </div>

          {/* Right-side icons: circular pencil + circular visibility */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
            <motion.div
              role="button"
              tabIndex={0}
              aria-label="edit event"
              whileTap={{ scale: 0.9 }}
              onClick={() => onEdit?.(event)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onEdit?.(event);
                }
              }}
              style={{
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                backgroundColor: THEME.inactivePill,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              <Pencil size={11} color="var(--color-content-muted)" />
            </motion.div>

            <VisibilityIcon
              visibility={event.visibility}
              isSubmitting={isSubmitting}
              onSubmit={handleSubmitForReview}
            />
          </div>
        </div>
      </motion.div>

      {pendingDelete && (
        <DeleteConfirmPortal
          label="Delete this event?"
          onConfirm={handleDeleteConfirmed}
          onCancel={() => setPendingDelete(false)}
        />
      )}
    </div>
  );
}

// ─── GCalEventRow ─────────────────────────────────────────────────────────────

function formatGCalDate(isoStr: string): string {
  const d = new Date(isoStr);
  const day = d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  const time = d
    .toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: true })
    .toUpperCase();
  return `${day} · ${time}`;
}

function GCalEventRow({ event }: { event: GCalEvent }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 16px 10px 20px',
        borderBottom: `1px solid ${THEME.listBorder}`,
        background: 'var(--color-surface-subtle)',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: '0.85rem',
            fontWeight: 'bold',
            letterSpacing: '1px',
            textTransform: 'lowercase',
            color: 'var(--color-content)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {event.title}
        </div>
        <div
          style={{
            fontSize: '0.72rem',
            color: 'var(--color-content-muted)',
            letterSpacing: '0.5px',
          }}
        >
          {formatGCalDate(event.start_datetime)}
        </div>
        {event.location && (
          <div
            style={{
              fontSize: '0.72rem',
              color: 'var(--color-content-muted)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {event.location}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
        <div
          style={{
            width: '28px',
            height: '28px',
            borderRadius: '50%',
            backgroundColor: THEME.inactivePill,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CalendarDays size={11} color="var(--color-content-muted)" />
        </div>
        {event.link && (
          <button
            onClick={e => {
              e.stopPropagation();
              window.open(event.link, '_blank', 'noopener,noreferrer');
            }}
            style={{
              width: '28px',
              height: '28px',
              borderRadius: '50%',
              backgroundColor: 'rgba(65, 100, 81,0.12)',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <ExternalLink size={11} color="var(--color-primary)" />
          </button>
        )}
      </div>
    </div>
  );
}

// ─── PinnedEventRow ───────────────────────────────────────────────────────────

function PinnedEventRow({ event }: { event: PinnedEvent }) {
  const dateDisplay = formatEventDateRange(event.start_date, event.expiry);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 16px 10px 20px',
        borderBottom: `1px solid ${THEME.listBorder}`,
        background: 'var(--color-surface-subtle)',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', marginBottom: '3px' }}>
          <span
            style={{
              fontSize: '0.85rem',
              fontWeight: 'bold',
              letterSpacing: '1px',
              textTransform: 'lowercase',
              color: 'var(--color-content)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flexShrink: 1,
              minWidth: 0,
            }}
          >
            {event.name}
          </span>
          <span
            style={{
              fontSize: '0.68rem',
              letterSpacing: '0.5px',
              textTransform: 'lowercase',
              color: 'var(--color-content-muted)',
              flexShrink: 0,
            }}
          >
            {event.category}
          </span>
        </div>
        {dateDisplay && (
          <div
            style={{
              fontSize: '0.72rem',
              color: 'var(--color-content-muted)',
              letterSpacing: '0.5px',
            }}
          >
            {dateDisplay}
          </div>
        )}
      </div>
      <div
        style={{
          width: '28px',
          height: '28px',
          borderRadius: '50%',
          backgroundColor: THEME.inactivePill,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Pin size={11} color="var(--color-content-muted)" />
      </div>
    </div>
  );
}

// ─── ListCard ─────────────────────────────────────────────────────────────────

interface ListCardProps {
  title: string;
  itemCount: number;
  authorName?: string;
  isActive?: boolean;
  isExpanded: boolean;
  onToggleExpand: () => void;
  items?: ActiveListItem[];
  onRemoveItem?: (itemId: number, listId: number) => void;
  onShare: () => void;
  onAddToFilter?: () => void;
  onRemoveFromFilter?: () => void;
  isFilter?: boolean;
  onRename?: (newTitle: string) => void;
  visibility?: 'private' | 'shared' | 'public';
  onToggleVisibility?: () => void;
  savedIndicator?: boolean;
  // PR2: swipe actions (user lists only)
  onSetActive?: () => void;
  onDelete?: () => void;
  autoFocusTitle?: boolean;
  // PR3: move item between lists
  listId?: number;
  otherLists?: CustomList[];
  onMoveItem?: (
    itemId: number,
    sourceListId: number,
    targetListId: number,
    item: ActiveListItem
  ) => void;
  onCreateList?: () => Promise<number>;
  // Outdated indicators
  isOutdated?: boolean;
  outdatedItemIds?: Set<number>;
}

function ListCard({
  title,
  itemCount,
  authorName,
  isActive,
  isExpanded,
  onToggleExpand,
  items,
  onRemoveItem,
  onShare,
  onAddToFilter,
  onRemoveFromFilter,
  isFilter,
  onRename,
  visibility,
  onToggleVisibility,
  savedIndicator,
  onSetActive,
  onDelete,
  autoFocusTitle,
  listId,
  otherLists,
  onMoveItem,
  onCreateList,
  isOutdated,
  outdatedItemIds,
}: ListCardProps) {
  const handleCreateAndMove = async (itemId: number, item: ActiveListItem) => {
    if (!onCreateList || !onMoveItem || listId === undefined) return;
    try {
      const newListId = await onCreateList();
      onMoveItem(itemId, listId, newListId, item);
    } catch {
      // error already toasted by handleCreateList in App.tsx
    }
  };
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [pendingDelete, setPendingDelete] = useState(false);
  const titleInputRef = useRef<HTMLInputElement>(null);

  // Auto-open title editor when newly created
  useEffect(() => {
    if (autoFocusTitle) {
      setTitleDraft(title);
      setIsEditingTitle(true);
    }
    // Only run on mount — autoFocusTitle is a one-time signal
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Swipe gesture (only when onSetActive/onDelete are provided) ──
  const x = useMotionValue(0);
  const swipeable = !!(onSetActive || onDelete);

  const setActiveUnderlayOpacity = useTransform(x, [0, CARD_SWIPE_COMMIT], [0, 1]);
  const deleteUnderlayOpacity = useTransform(x, [-CARD_SWIPE_COMMIT, 0], [1, 0]);

  const handleCardDragEnd = (_: any, info: { offset: { x: number } }) => {
    if (info.offset.x >= CARD_SWIPE_COMMIT && onSetActive) {
      animate(x, 0, { type: 'spring', stiffness: 400, damping: 35 });
      onSetActive();
    } else if (info.offset.x <= -CARD_SWIPE_COMMIT && onDelete) {
      animate(x, 0, { type: 'spring', stiffness: 400, damping: 35 });
      setPendingDelete(true);
    } else {
      animate(x, 0, { type: 'spring', stiffness: 400, damping: 35 });
    }
  };

  const commitTitle = () => {
    const t = titleDraft.trim();
    if (t.length >= 3) onRename?.(t);
    setIsEditingTitle(false);
  };

  return (
    <div style={{ position: 'relative', overflow: 'hidden' }}>
      {/* ── Swipe underlay ── */}
      {swipeable && (
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
          {/* Right-swipe: set active (teal) — fills full width */}
          <motion.div
            style={{
              opacity: setActiveUnderlayOpacity,
              background: THEME.activePill,
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              paddingLeft: '20px',
              gap: '8px',
            }}
          >
            <Check size={14} color="var(--color-content-inverse)" strokeWidth={2.5} />
            <span
              style={{
                fontSize: '0.75rem',
                letterSpacing: '1px',
                color: 'var(--color-content-inverse)',
                textTransform: 'lowercase',
              }}
            >
              set active
            </span>
          </motion.div>
          {/* Left-swipe: delete (red) — fills full width */}
          <motion.div
            style={{
              opacity: deleteUnderlayOpacity,
              background: 'var(--color-error)',
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              paddingRight: '20px',
              gap: '8px',
            }}
          >
            <span
              style={{
                fontSize: '0.75rem',
                letterSpacing: '1px',
                color: 'var(--color-content-inverse)',
                textTransform: 'lowercase',
              }}
            >
              delete
            </span>
            <Trash2 size={14} color="var(--color-content-inverse)" />
          </motion.div>
        </div>
      )}

      {/* ── Card content (draggable for user lists, static for curated) ── */}
      <motion.div
        drag={swipeable ? 'x' : false}
        dragConstraints={{ left: -150, right: 150 }}
        dragElastic={0.15}
        style={{
          x: swipeable ? x : undefined,
          background: isOutdated ? THEME.outdatedBg : 'var(--color-surface)',
          borderLeft: isActive ? `3px solid ${THEME.activePill}` : '3px solid transparent',
          boxShadow: 'var(--shadow-sm)',
          transition: 'border-left 0.25s ease',
          position: 'relative',
        }}
        onDragEnd={swipeable ? handleCardDragEnd : undefined}
      >
        <div
          style={{
            borderBottom: `1px solid ${THEME.listBorder}`,
            padding: '12px 16px 12px 13px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '10px',
          }}
        >
          {/* Title + subtitle */}
          <div style={{ flex: 1, cursor: 'pointer', minWidth: 0 }} onClick={onToggleExpand}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              {isEditingTitle ? (
                <input
                  ref={titleInputRef}
                  value={titleDraft}
                  onChange={e => setTitleDraft(e.target.value)}
                  onBlur={commitTitle}
                  onKeyDown={e => {
                    if (e.key === 'Enter') commitTitle();
                    else if (e.key === 'Escape') setIsEditingTitle(false);
                  }}
                  onClick={e => e.stopPropagation()}
                  autoFocus
                  style={{
                    flex: 1,
                    fontSize: '0.9rem',
                    letterSpacing: '1px',
                    textTransform: 'lowercase',
                    color: 'var(--color-content)',
                    background: 'transparent',
                    border: 'none',
                    borderBottom: `1px solid ${THEME.activePill}`,
                    outline: 'none',
                    padding: '2px 0',
                    minWidth: 0,
                  }}
                />
              ) : (
                <span
                  style={{
                    fontSize: '0.9rem',
                    letterSpacing: '1px',
                    textTransform: 'lowercase',
                    color: 'var(--color-content)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {title}
                </span>
              )}
              {savedIndicator && !isEditingTitle && (
                <Check
                  size={12}
                  color={THEME.activePill}
                  strokeWidth={2.5}
                  style={{ flexShrink: 0 }}
                />
              )}
              {onRename && !isEditingTitle && (
                <Pencil
                  size={11}
                  color="var(--color-content-muted)"
                  style={{ cursor: 'pointer', flexShrink: 0 }}
                  onClick={e => {
                    e.stopPropagation();
                    setTitleDraft(title);
                    setIsEditingTitle(true);
                  }}
                />
              )}
            </div>
            <div
              style={{
                fontSize: '0.72rem',
                color: 'var(--color-content-muted)',
                letterSpacing: '0.5px',
                marginTop: '2px',
              }}
            >
              {itemCount} {itemCount === 1 ? 'place' : 'places'}
              {authorName ? ` · by ${authorName}` : ''}
            </div>
          </div>

          {/* Action buttons */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              flexShrink: 0,
              marginTop: '1px',
            }}
          >
            {/* Visibility toggle (my lists only) */}
            {onToggleVisibility && (
              <motion.div
                role="button"
                tabIndex={0}
                aria-label={visibility === 'public' ? 'make private' : 'make public'}
                whileTap={{ scale: 0.9 }}
                onClick={e => {
                  e.stopPropagation();
                  onToggleVisibility();
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    onToggleVisibility();
                  }
                }}
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  backgroundColor: THEME.inactivePill,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                }}
              >
                {visibility === 'public' ? (
                  <Globe size={13} color={THEME.inactiveText} />
                ) : (
                  <Lock size={13} color={THEME.inactiveText} />
                )}
              </motion.div>
            )}

            {/* Share — hidden for private user lists */}
            {(!onToggleVisibility || visibility === 'public') && (
              <motion.div
                role="button"
                tabIndex={0}
                aria-label="share list"
                whileTap={{ scale: 0.9 }}
                onClick={e => {
                  e.stopPropagation();
                  onShare();
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    onShare();
                  }
                }}
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  backgroundColor: THEME.inactivePill,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                }}
              >
                <Share2 size={13} color={THEME.inactiveText} />
              </motion.div>
            )}

            {/* Add/remove filter */}
            {onAddToFilter && (
              <motion.div
                role="button"
                tabIndex={0}
                aria-label={isFilter ? 'remove from filter' : 'add to filter'}
                aria-pressed={isFilter}
                whileTap={{ scale: 0.9 }}
                onClick={e => {
                  e.stopPropagation();
                  if (isFilter) {
                    onRemoveFromFilter?.();
                  } else {
                    onAddToFilter();
                  }
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    if (isFilter) {
                      onRemoveFromFilter?.();
                    } else {
                      onAddToFilter();
                    }
                  }
                }}
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  backgroundColor: isFilter ? 'rgba(220, 50, 50, 0.12)' : THEME.inactivePill,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                }}
              >
                {isFilter ? (
                  <Minus size={13} color="var(--color-error)" />
                ) : (
                  <Plus size={13} color={THEME.inactiveText} />
                )}
              </motion.div>
            )}

            {/* Expand chevron */}
            <motion.div
              animate={{ rotate: isExpanded ? 180 : 0 }}
              transition={{ duration: 0.2 }}
              style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', padding: '4px' }}
              onClick={e => {
                e.stopPropagation();
                onToggleExpand();
              }}
            >
              <ChevronDown size={15} color="var(--color-content-muted)" />
            </motion.div>
          </div>
        </div>

        {/* Expanded item rows */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              style={{ overflow: 'hidden' }}
            >
              {items === undefined ? (
                <div
                  style={{
                    padding: '10px 16px 10px 20px',
                    fontSize: '0.75rem',
                    color: 'var(--color-content-muted)',
                    letterSpacing: '1px',
                  }}
                >
                  loading…
                </div>
              ) : items.length === 0 ? (
                <div
                  style={{
                    padding: '10px 16px 10px 20px',
                    fontSize: '0.75rem',
                    color: 'var(--color-content-muted)',
                    letterSpacing: '1px',
                  }}
                >
                  no places yet
                </div>
              ) : (
                items.map(item => (
                  <MyListRow
                    key={item.id}
                    item={item}
                    listId={listId ?? 0}
                    otherLists={otherLists ?? []}
                    onRemove={onRemoveItem ? () => onRemoveItem(item.id, listId ?? 0) : undefined}
                    onMove={
                      onMoveItem && listId !== undefined
                        ? targetListId => onMoveItem(item.id, listId, targetListId, item)
                        : undefined
                    }
                    onCreateAndMove={
                      onMoveItem && onCreateList && listId !== undefined
                        ? () => handleCreateAndMove(item.id, item)
                        : undefined
                    }
                    isOutdated={outdatedItemIds?.has(item.id)}
                  />
                ))
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {pendingDelete && (
        <DeleteConfirmPortal
          label="Delete this list?"
          onConfirm={() => {
            setPendingDelete(false);
            animate(x, -400, { duration: 0.2, ease: 'easeIn', onComplete: () => onDelete?.() });
          }}
          onCancel={() => setPendingDelete(false)}
        />
      )}
    </div>
  );
}

// ─── BottomSheet ──────────────────────────────────────────────────────────────

const BottomSheet = forwardRef<BottomSheetHandle, BottomSheetProps>(function BottomSheet(
  {
    isMobile: _isMobile,
    filterPresets,
    onTogglePreset,
    currentDate,
    onDateChange,
    availableDates,
    curatedLists,
    onAddList,
    onRemovePreset,
    onShareList,
    onHeightChange: _onHeightChange,
    shareViewState,
    userLists,
    listItemsCache,
    onExpandList,
    curatedItemsCache,
    onExpandCuratedList,
    activeListId,
    savedIndicatorListId,
    onCreateList,
    onDeleteList,
    onSetActiveList,
    onAddUserList,
    onRemoveItem,
    onMoveItem,
    onRenameList,
    onToggleVisibility,
    userEvents,
    pinnedEvents,
    onEditEvent,
    onDeleteUserEvent,
    onSubmitForReview,
    onRefreshEvents,
    calendarEvents,
    calendarEventsLoading,
    calendarEventsError,
    onCalendarReconnect,
    mapVendors,
    isInitialLoad,
    isVendorFeedLoading,
    selectedVendorId,
    selectedDisplayIds,
    onVendorSelect,
    activeListItems,
    showToast,
    onSignUpModalRequest,
    onShareEvent,
    onPinEvent,
    onUnpinEvent,
  },
  ref
) {
  const [level, setLevel] = useState<SheetLevel>(1);
  const [activeTab, setActiveTab] = useState<ListTab>('discover');
  const [expandedListIds, setExpandedListIds] = useState<Set<number>>(new Set());
  const [expandedCuratedUuids, setExpandedCuratedUuids] = useState<Set<string>>(new Set());
  const [autoFocusListId, setAutoFocusListId] = useState<number | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  // Discover tab: virtual scroll window
  const discoverScrollRef = useRef<HTMLDivElement>(null);
  const [discoverLoadedCards, setDiscoverLoadedCards] = useState(40);

  useEffect(() => {
    setDiscoverLoadedCards(40);
  }, [currentDate]);

  const handleDiscoverScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const container = e.currentTarget;
      const pct = (container.scrollTop + container.clientHeight) / container.scrollHeight;
      if (pct > 0.9 && discoverLoadedCards < 80) {
        setDiscoverLoadedCards(prev => Math.min(prev + 10, 80));
      }
    },
    [discoverLoadedCards]
  );

  const handleDiscoverVendorClick = useCallback(
    (vendorId: number, displayId: string) => {
      const vendor = mapVendors.find(v => v.id === vendorId);
      if (vendor?.coordinates && (window as any).handlePanelCardClick) {
        (window as any).handlePanelCardClick(vendorId, displayId, vendor.coordinates, true);
      }
      onVendorSelect(vendorId, displayId);
    },
    [mapVendors, onVendorSelect]
  );

  useImperativeHandle(ref, () => ({
    openToLevel: (l: SheetLevel) => {
      setLevel(l);
      if (l === 2) setActiveTab('lists');
    },
    switchToTab: (tab: ListTab) => {
      setActiveTab(tab);
    },
  }));

  useEffect(() => {
    if (activeTab === 'myevents') onRefreshEvents?.();
  }, [activeTab]);

  useEffect(() => {
    const timer = setTimeout(() => setLevel(prev => (prev <= 1 ? 0 : prev)), 6000);
    return () => clearTimeout(timer);
  }, []);

  const dragY = useMotionValue(0);

  const activeListUuids = filterPresets.filter(p => p.type === 'list').map(p => p.value);

  const visiblePresets = filterPresets.slice(0, VISIBLE_PILLS);

  const handleDragEnd = (_: any, info: { offset: { y: number }; velocity: { y: number } }) => {
    const vel = info.velocity.y;
    // Require the velocity to not be strongly opposed to the offset direction.
    // This prevents accidental level changes from taps or residual momentum.
    if (info.offset.y < -SWIPE_THRESHOLD && vel < 200) {
      setLevel(prev => (prev < 2 ? ((prev + 1) as SheetLevel) : 2));
    } else if (info.offset.y > SWIPE_THRESHOLD && vel > -200) {
      setLevel(prev => (prev > 0 ? ((prev - 1) as SheetLevel) : 0));
    }
    dragY.set(0);
  };

  const handlePlusClick = () => setLevel(prev => (prev === 2 ? 1 : 2));

  const toggleListExpand = (id: number) => {
    setExpandedListIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
        onExpandList(id);
      }
      return next;
    });
  };

  const toggleCuratedExpand = (uuid: string) => {
    setExpandedCuratedUuids(prev => {
      const next = new Set(prev);
      if (next.has(uuid)) {
        next.delete(uuid);
      } else {
        next.add(uuid);
        onExpandCuratedList(uuid);
      }
      return next;
    });
  };

  const handleCreateClick = async () => {
    if (isCreating) return;
    setIsCreating(true);
    try {
      const newId = await onCreateList();
      setAutoFocusListId(newId);
      // Clear after one render cycle so the effect only fires once
      setTimeout(() => setAutoFocusListId(null), 100);
    } catch {
      // error toasted by App.tsx
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <motion.div
      role="complementary"
      aria-label="map filters"
      drag="y"
      dragConstraints={{ top: 0, bottom: 0 }}
      dragElastic={0.2}
      onDragEnd={handleDragEnd}
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9990,
        borderRadius: THEME.borderRadius,
        boxShadow: THEME.shadow,

        maxHeight: '65vh',
        touchAction: 'none',
        overflow: 'hidden',
      }}
    >
      {/* Glass section — handle + filter pills only */}
      <div className="ceen-glass" style={{ borderRadius: THEME.borderRadius }}>
        {/* Drag handle */}
        <div
          role="button"
          tabIndex={0}
          aria-label={level === 0 ? 'expand panel' : 'collapse panel'}
          onClick={() => setLevel(prev => (prev === 0 ? 1 : 0))}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') setLevel(prev => (prev === 0 ? 1 : 0));
          }}
          style={{
            display: 'flex',
            justifyContent: 'center',
            paddingTop: '6px',
            paddingBottom: '4px',
            cursor: 'pointer',
            minHeight: 'unset',
          }}
        >
          <div
            style={{ width: '36px', height: '4px', borderRadius: '2px', background: THEME.handle }}
          />
        </div>

        {/* Filter pill row */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            paddingLeft: '16px',
            paddingRight: '16px',
            paddingTop: '6px',
            paddingBottom: '8px',
          }}
        >
          {visiblePresets.map(preset => (
            <FilterPill
              key={preset.id}
              preset={preset}
              onToggle={() => onTogglePreset(preset.id)}
            />
          ))}
          <motion.div
            role="button"
            tabIndex={0}
            aria-label={level === 2 ? 'close lists panel' : 'open lists panel'}
            aria-expanded={level === 2}
            whileTap={{ scale: 0.93 }}
            onClick={handlePlusClick}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handlePlusClick();
              }
            }}
            style={{
              backgroundColor: level === 2 ? THEME.activePill : THEME.inactivePill,
              borderRadius: '20px',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              flexShrink: 0,
              marginLeft: 'auto',
              boxShadow: 'var(--shadow-sm)',
              transition: 'background-color 0.2s ease',
              minHeight: 'unset',
              minWidth: 'unset',
            }}
          >
            <List
              size={16}
              color={level === 2 ? 'var(--color-content-inverse)' : THEME.inactiveText}
            />
          </motion.div>
        </div>
      </div>
      {/* end glass section */}

      {/* Level 1: Date strip */}
      <AnimatePresence>
        {level >= 1 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            style={{ overflow: 'hidden' }}
          >
            <div
              style={{
                background: 'var(--color-surface-subtle)',
                borderTop: `1px solid ${THEME.listBorder}`,
              }}
            >
              <DateStrip
                currentDate={currentDate}
                onDateChange={onDateChange}
                availableDates={availableDates}
                shareViewState={shareViewState}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Level 2: List panel */}
      <AnimatePresence>
        {level === 2 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: LIST_PANEL_HEIGHT }}
            exit={{ opacity: 0, height: 0 }}
            style={{
              overflow: 'hidden',
              borderTop: `1px solid ${THEME.listBorder}`,
              background: 'var(--color-surface-subtle)',
            }}
          >
            {/* Tab header */}
            <div
              style={{
                padding: '8px 16px 0',
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'space-between',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  gap: '0',
                  flex: 1,
                  justifyContent: 'space-evenly',
                  borderBottom: `2px solid ${THEME.listBorder}`,
                }}
              >
                {(['discover', 'lists', 'myevents'] as ListTab[]).map(tab => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    style={{
                      background: 'transparent',
                      color:
                        activeTab === tab ? 'var(--color-primary)' : 'var(--color-content-muted)',
                      border: 'none',
                      borderBottom:
                        activeTab === tab
                          ? '2px solid var(--color-primary)'
                          : '2px solid transparent',
                      borderRadius: 0,
                      marginBottom: '-2px',
                      padding: '8px 12px 8px',
                      fontSize: '0.75rem',
                      lineHeight: '1',
                      letterSpacing: '1px',
                      textTransform: 'lowercase',
                      cursor: 'pointer',
                      transition: 'color 0.2s ease, border-color 0.2s ease',
                      minHeight: 'unset',
                      minWidth: 'unset',
                    }}
                  >
                    {tab === 'lists' ? 'lists' : tab === 'discover' ? 'discover' : 'my events'}
                  </button>
                ))}
              </div>
            </div>

            {/* Scrollable tab content */}
            <div
              style={{
                overflowY: 'auto',
                height: 'calc(100% - 44px)',
                background: THEME.listBg,
                marginTop: '4px',
              }}
            >
              {activeTab === 'discover' ? (
                isInitialLoad ? (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '32px 16px',
                    }}
                  >
                    <div className="w-7 h-7 rounded-full border-4 border-gray-200 border-t-gray-500 animate-spin" />
                    <p
                      style={{
                        fontSize: '0.8rem',
                        color: 'var(--color-content-muted)',
                        letterSpacing: '1px',
                      }}
                    >
                      loading things to do!
                    </p>
                  </div>
                ) : (
                  <div
                    ref={discoverScrollRef}
                    onScroll={handleDiscoverScroll}
                    style={{
                      overflowY: 'auto',
                      height: '100%',
                      WebkitOverflowScrolling: 'touch',
                      overscrollBehavior: 'contain',
                      position: 'relative',
                    }}
                  >
                    {isVendorFeedLoading && (
                      <div
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: 'rgba(100,100,100,0.6)',
                          borderRadius: '8px',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '10px',
                          zIndex: 10,
                        }}
                      >
                        <div className="w-6 h-6 rounded-full border-4 border-gray-200 border-t-white animate-spin" />
                        <p style={{ fontSize: '0.75rem', color: '#fff', letterSpacing: '1px' }}>
                          updating community to-dos
                        </p>
                      </div>
                    )}
                    {mapVendors.length > 0 ? (
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, var(--card-width))',
                          gap: 'var(--card-gap)',
                          justifyContent: 'space-evenly',
                          padding: '12px 8px 24px',
                        }}
                      >
                        {mapVendors.slice(0, discoverLoadedCards).map(vendor => (
                          <VendorCard
                            key={vendor.displayID}
                            vendor={vendor}
                            isSelected={selectedDisplayIds.includes(vendor.displayID)}
                            isPopup={false}
                            selection={{
                              vendorId: selectedVendorId,
                              currentDateDisplayId:
                                selectedDisplayIds.find(id =>
                                  id.includes(format(currentDate, 'yyyy-MM-dd'))
                                ) || null,
                              allDisplayIds: selectedDisplayIds,
                            }}
                            onClick={() => handleDiscoverVendorClick(vendor.id, vendor.displayID)}
                            onSignUpModalRequest={onSignUpModalRequest}
                            onShareEvent={onShareEvent}
                            showToast={showToast}
                            activeListItems={activeListItems}
                            onPinEvent={onPinEvent}
                            onUnpinEvent={onUnpinEvent}
                          />
                        ))}
                      </div>
                    ) : (
                      !isVendorFeedLoading && (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'flex-end',
                            justifyContent: 'center',
                            height: '100%',
                            paddingBottom: '40%',
                          }}
                        >
                          <div
                            style={{
                              padding: '40px',
                              textAlign: 'center',
                              color: 'var(--color-content)',
                            }}
                          >
                            <p
                              style={{
                                fontSize: '1rem',
                                fontWeight: 500,
                                marginTop: '4px',
                                letterSpacing: '1px',
                              }}
                            >
                              no events to track here!
                            </p>
                            <p
                              style={{
                                fontSize: '0.9rem',
                                padding: '16px 0',
                                letterSpacing: '0.5px',
                              }}
                            >
                              clue us in at
                            </p>
                            <div
                              style={{
                                backgroundColor: 'rgba(65, 100, 81, 1)',
                                borderRadius: '6px',
                                padding: '6px 12px',
                                display: 'inline-block',
                              }}
                            >
                              <a
                                href="mailto:posters@ceen.live?subject=Let's connect about sharing events&body=Hi there!%0D%0A%0D%0AI want to share my events with you! How can I get in touch and speak 1:1?"
                                style={{
                                  fontSize: '1rem',
                                  fontWeight: 500,
                                  color: '#fff',
                                  textDecoration: 'none',
                                }}
                              >
                                posters@ceen.live
                              </a>
                            </div>
                          </div>
                        </div>
                      )
                    )}
                    {(() => {
                      const currentDateStr = format(currentDate, 'yyyy-MM-dd');
                      const todaysGCalEvents = (calendarEvents ?? []).filter(e => {
                        const eventDate = e.start_datetime.slice(0, 10);
                        return eventDate === currentDateStr;
                      });
                      return todaysGCalEvents.length > 0 ? (
                        <>
                          <div
                            style={{
                              padding: '8px 16px 4px',
                              fontSize: '0.68rem',
                              letterSpacing: '1.5px',
                              textTransform: 'uppercase',
                              color: 'var(--color-content-muted)',
                              background: THEME.listBg,
                            }}
                          >
                            from your calendar
                          </div>
                          {todaysGCalEvents.map(e => (
                            <GCalEventRow key={e.gcal_id} event={e} />
                          ))}
                        </>
                      ) : null;
                    })()}
                    <div
                      style={{
                        padding: '12px 16px',
                        fontSize: '0.72rem',
                        color: 'var(--color-content-muted)',
                        textAlign: 'center',
                        letterSpacing: '0.5px',
                      }}
                    >
                      change the date to see more
                    </div>
                  </div>
                )
              ) : activeTab === 'myevents' ? (
                <>
                  <div
                    style={{
                      padding: '10px 16px 4px',
                      textAlign: 'center',
                      fontSize: '0.75rem',
                      color: 'var(--color-content-muted)',
                      letterSpacing: '0.5px',
                    }}
                  >
                    long-press the map to add an event.
                  </div>
                  {(!userEvents || userEvents.length === 0) && (
                    <div
                      style={{
                        padding: '12px 16px',
                        textAlign: 'center',
                        fontSize: '0.8rem',
                        color: 'var(--color-content-muted)',
                        letterSpacing: '1px',
                      }}
                    >
                      no events yet.
                    </div>
                  )}
                  {userEvents &&
                    userEvents.length > 0 &&
                    userEvents.map(event => (
                      <MyEventRow
                        key={event.id}
                        event={event}
                        onEdit={onEditEvent}
                        onDelete={onDeleteUserEvent}
                        onSubmitForReview={onSubmitForReview}
                      />
                    ))}
                  {/* "pinned events" section — vendors from user's lists */}
                  {pinnedEvents && pinnedEvents.length > 0 && (
                    <>
                      <div
                        style={{
                          padding: '8px 16px 4px',
                          fontSize: '0.68rem',
                          letterSpacing: '1.5px',
                          textTransform: 'uppercase',
                          color: 'var(--color-content-muted)',
                          background: THEME.listBg,
                          position: 'sticky',
                          top: 0,
                          zIndex: 1,
                        }}
                      >
                        pinned events
                      </div>
                      {pinnedEvents.map(e => (
                        <PinnedEventRow key={e.id} event={e} />
                      ))}
                    </>
                  )}
                  {/* "from your calendar" section — only visible when loading, error, or has events */}
                  {(calendarEventsLoading ||
                    !!calendarEventsError ||
                    (calendarEvents && calendarEvents.length > 0)) && (
                    <>
                      <div
                        style={{
                          padding: '8px 16px 4px',
                          fontSize: '0.68rem',
                          letterSpacing: '1.5px',
                          textTransform: 'uppercase',
                          color: 'var(--color-content-muted)',
                          background: THEME.listBg,
                          position: 'sticky',
                          top: 0,
                          zIndex: 1,
                        }}
                      >
                        from your calendar
                      </div>
                      {calendarEventsLoading &&
                        [0, 1, 2].map(i => (
                          <div
                            key={i}
                            style={{
                              height: 56,
                              margin: '4px 16px',
                              background: 'var(--color-surface-subtle)',
                              borderRadius: 8,
                            }}
                          />
                        ))}
                      {!calendarEventsLoading && calendarEventsError && (
                        <div
                          style={{
                            padding: '12px 16px',
                            fontSize: '0.8rem',
                            color: 'var(--color-content-muted)',
                            letterSpacing: '0.5px',
                          }}
                        >
                          {calendarEventsError === 'calendar_token_expired'
                            ? 'calendar access expired'
                            : 'calendar unavailable'}{' '}
                          —{' '}
                          <span
                            role="button"
                            onClick={onCalendarReconnect}
                            style={{ textDecoration: 'underline', cursor: 'pointer' }}
                          >
                            reconnect
                          </span>
                        </div>
                      )}
                      {!calendarEventsLoading &&
                        calendarEvents?.map(e => <GCalEventRow key={e.gcal_id} event={e} />)}
                    </>
                  )}
                </>
              ) : (
                /* lists tab — my lists + community lists */
                <>
                  {/* Section: My Lists */}
                  <div
                    style={{
                      padding: '8px 16px 4px',
                      fontSize: '0.68rem',
                      letterSpacing: '1.5px',
                      textTransform: 'uppercase',
                      color: 'var(--color-content-muted)',
                      background: THEME.listBg,
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                    }}
                  >
                    my lists
                  </div>
                  {/* Ghost "create a list" card */}
                  <motion.div
                    role="button"
                    tabIndex={0}
                    aria-label="create a new list"
                    whileTap={{ scale: 0.97 }}
                    onClick={isCreating ? undefined : handleCreateClick}
                    onKeyDown={e => {
                      if ((e.key === 'Enter' || e.key === ' ') && !isCreating) {
                        e.preventDefault();
                        handleCreateClick();
                      }
                    }}
                    style={{
                      padding: '12px 16px',
                      borderBottom: `1px solid ${THEME.listBorder}`,
                      borderLeft: `3px dashed ${THEME.inactivePill}`,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      cursor: isCreating ? 'default' : 'pointer',
                      opacity: isCreating ? 0.5 : 1,
                      background: 'var(--color-surface)',
                      boxShadow: 'var(--shadow-sm)',
                      transition: 'opacity 0.2s ease',
                      minHeight: 'unset',
                      minWidth: 'unset',
                    }}
                  >
                    <Plus size={13} color={THEME.activePill} strokeWidth={2} />
                    <span
                      style={{
                        fontSize: '0.85rem',
                        letterSpacing: '1px',
                        textTransform: 'lowercase',
                        color: 'var(--color-content-muted)',
                      }}
                    >
                      create a list
                    </span>
                  </motion.div>
                  {userLists.length === 0 ? (
                    <div
                      style={{
                        padding: '12px 16px 4px',
                        fontSize: '0.8rem',
                        color: 'var(--color-content-muted)',
                        letterSpacing: '1px',
                      }}
                    >
                      tap the pin on any event to save it here
                    </div>
                  ) : (
                    userLists.map(list => {
                      const isExpanded = expandedListIds.has(list.id);
                      const items = listItemsCache[list.id];
                      const isFilter = activeListUuids.includes(list.uuid);
                      const outdatedItemIds = items
                        ? new Set(items.filter(isActiveListItemOutdated).map(i => i.id))
                        : undefined;
                      const isListOutdated =
                        !!items && items.length > 0 && outdatedItemIds!.size === items.length;
                      return (
                        <ListCard
                          key={list.id}
                          title={list.title || 'my list'}
                          itemCount={items ? items.length : (list.items_count ?? 0)}
                          isActive={list.id === activeListId}
                          isExpanded={isExpanded}
                          onToggleExpand={() => toggleListExpand(list.id)}
                          items={isExpanded ? items : undefined}
                          onRemoveItem={onRemoveItem}
                          onShare={() => onShareList(list.uuid)}
                          onAddToFilter={() => onAddUserList(list)}
                          onRemoveFromFilter={() => onRemovePreset(list.uuid)}
                          isFilter={isFilter}
                          onRename={newTitle => onRenameList(list.id, newTitle)}
                          visibility={list.visibility ?? 'private'}
                          onToggleVisibility={() => onToggleVisibility(list.id)}
                          savedIndicator={savedIndicatorListId === list.id}
                          onSetActive={() => onSetActiveList(list.id)}
                          onDelete={() => onDeleteList(list.id)}
                          autoFocusTitle={list.id === autoFocusListId}
                          listId={list.id}
                          otherLists={userLists.filter(l => l.id !== list.id)}
                          onMoveItem={onMoveItem}
                          onCreateList={onCreateList}
                          isOutdated={isListOutdated}
                          outdatedItemIds={outdatedItemIds}
                        />
                      );
                    })
                  )}

                  {/* Section: Community Lists */}
                  <div
                    style={{
                      padding: '8px 16px 4px',
                      marginTop: '8px',
                      fontSize: '0.68rem',
                      letterSpacing: '1.5px',
                      textTransform: 'uppercase',
                      color: 'var(--color-content-muted)',
                      background: THEME.listBg,
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                    }}
                  >
                    community lists
                  </div>
                  {curatedLists.length === 0 ? (
                    <div
                      style={{
                        padding: '12px 16px',
                        fontSize: '0.8rem',
                        color: 'var(--color-content-muted)',
                        letterSpacing: '1px',
                      }}
                    >
                      no lists available yet
                    </div>
                  ) : (
                    curatedLists.map(list => {
                      const isExpanded = expandedCuratedUuids.has(list.uuid);
                      const isFilter = activeListUuids.includes(list.uuid);
                      const items = curatedItemsCache[list.uuid];
                      return (
                        <ListCard
                          key={list.uuid}
                          title={list.title}
                          itemCount={isExpanded && items ? items.length : list.item_count}
                          authorName={list.display_name || 'ceen'}
                          isExpanded={isExpanded}
                          onToggleExpand={() => toggleCuratedExpand(list.uuid)}
                          items={isExpanded ? items : undefined}
                          onShare={() => onShareList(list.uuid)}
                          onAddToFilter={() => onAddList(list)}
                          onRemoveFromFilter={() => onRemovePreset(list.uuid)}
                          isFilter={isFilter}
                        />
                      );
                    })
                  )}
                </>
              )}
              <div style={{ height: '28px', flexShrink: 0 }} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
});

export default BottomSheet;
